import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { articleFeedbackQuery, rateArticle, rateSolution, solutionFeedbackQuery, type FeedbackMarks } from '../api/easeedeskSolutions'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'

/**
 * Notele unei solutii - `solutions/_feedback.php` (`Solutions::feedback`,
 * `rate`): media cu stele, nota mea (cinci stele de apasat), comentariul,
 * si ultimele 25 de pareri. Se cere abia cand se desface cardul, ca in fila
 * veche (`.tsol-fb` incarcat prin ajax): zeci de solutii pe fila, si notele
 * fiecareia din pornire ar fi zeci de interogari.
 *
 * Acelasi panou sta pe fila tichetului, in biblioteca de solutii si pe
 * articolul Self-Service (`article` in loc de `subcategory`): aceleasi note,
 * acelasi tabel, aceeasi poarta pe server.
 */

function Stars({ value, size = 12 }: { value: number; size?: number }) {
  const out = []
  for (let i = 1; i <= 5; i++) {
    const icon = value >= i ? 'fas fa-star' : value >= i - 0.5 ? 'fas fa-star-half-alt' : 'far fa-star'
    out.push(<i key={i} className={icon} style={{ fontSize: size }} aria-hidden="true"></i>)
  }
  return <>{out}</>
}

export function SolutionFeedback({ subcategory = 0, article = 0, ticket = 0 }: { subcategory?: number; article?: number; ticket?: number }) {
  const onArticle = article > 0
  const solution = useQuery({ ...solutionFeedbackQuery(subcategory), enabled: !onArticle })
  const onPage = useQuery({ ...articleFeedbackQuery(article), enabled: onArticle })
  const q: { isError: boolean; data: FeedbackMarks | undefined } = onArticle ? onPage : solution
  const queryKey = onArticle ? articleFeedbackQuery(article).queryKey : solutionFeedbackQuery(subcategory).queryKey
  const client = useQueryClient()
  const [mark, setMark] = useState(0)
  const [comment, setComment] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (q.isError) return <div className="alert alert-danger">The marks could not be loaded.</div>
  if (!q.data) return <Spinner />
  const data = q.data
  const myMark = mark > 0 ? mark : (data.mine?.rating ?? 0)
  const myComment = comment ?? data.mine?.comment ?? ''

  const send = () => {
    if (myMark < 1) {
      toast('Pick a mark between 1 and 5.', 'Feedback', 'error')
      return
    }
    setBusy(true)
    const sent = onArticle ? rateArticle(article, myMark, myComment) : rateSolution(subcategory, myMark, myComment, ticket)
    sent
      .then((reply) => {
        toast(reply.message, 'Feedback', 'success')
        void client.invalidateQueries({ queryKey })
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not recorded', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="sfb" data-subcategory={onArticle ? undefined : subcategory} data-article={onArticle ? article : undefined}>
      <div className="sfb-head">
        <span className="sfb-title">{onArticle ? 'How helpful is this article?' : 'How good is this solution?'}</span>
        <span className="sfb-avg" title={`${data.summary.count} mark${data.summary.count === 1 ? '' : 's'}`}>
          <span className="sfb-stars">
            <Stars value={data.summary.avg} size={13} />
          </span>{' '}
          <strong>{data.summary.avg.toFixed(1)}</strong> <span className="text-muted">({data.summary.count})</span>
        </span>
      </div>
      {data.storage ? (
        <div className="sfb-form">
          <div className="sfb-pick">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" className={`sfb-star${myMark >= i ? ' is-on' : ''}`} title={`${i} of 5`} onClick={() => setMark(i)}>
              <i className={`${myMark >= i ? 'fas' : 'far'} fa-star`} aria-hidden="true"></i>
            </button>
          ))}
          </div>
          <textarea className="form-control input-sm sfb-text" rows={2} placeholder="What worked, what did not, what is out of date" value={myComment} onChange={(ev) => setComment(ev.target.value)}></textarea>
          <button type="button" className="btn btn-primary btn-xs sfb-send" disabled={busy} onClick={send}>
            <i className="fas fa-paper-plane" aria-hidden="true"></i> {data.mine ? 'Update my mark' : 'Send'}
          </button>
        </div>
      ) : (
        <p className="text-muted">The feedback table does not exist yet.</p>
      )}
      {data.entries.length > 0 && (
        <ul className="sfb-list">
          {data.entries.map((e) => (
            <li key={e.id} className="sfb-entry">
              <span className="sfb-entry-stars">
                <Stars value={e.rating} size={11} />
              </span>{' '}
              <span className="sfb-entry-who">{e.author || 'Somebody'}</span> <span className="sfb-entry-when">{e.created}</span>
              {e.ticket > 0 && <span className="sfb-entry-ticket"> ticket #{e.ticket}</span>}
              {e.comment !== '' && <div className="sfb-entry-text">{e.comment}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
